import React, { useEffect, useRef } from 'react';
import { roadmapSkills } from './data';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Roadmap.scss';

gsap.registerPlugin(ScrollTrigger);

const Roadmap = () => {
    const roadmapRef = useRef(null);
    const titleRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                {
                    y: 50,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: 'power3.out'
                }
            );

            // Cards stagger animation
            const cards = document.querySelectorAll('.roadmap-card');
            gsap.fromTo(cards,
                {
                    y: 60,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: roadmapRef.current,
                        start: 'top 70%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 0.6,
                    stagger: 0.08,
                    ease: 'power3.out'
                }
            );

            // Year badges animation
            const yearBadges = document.querySelectorAll('.year-badge');
            gsap.fromTo(yearBadges,
                {
                    scale: 0.8,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: roadmapRef.current,
                        start: 'top 70%',
                    },
                    scale: 1,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.2,
                    ease: 'back.out(1.7)'
                }
            );

        }, roadmapRef);

        return () => ctx.revert();
    }, []);

    // Group skills by year
    const skillsByYear = roadmapSkills.reduce((acc, skill) => {
        const year = skill.timeline.includes('2026') ? '2026' : '2025';
        if (!acc[year]) acc[year] = [];
        acc[year].push(skill);
        return acc;
    }, {});

    return (
        <section className="roadmap" ref={roadmapRef}>
            <div className="roadmap-container">
                <h2 className="section-title" ref={titleRef}>
                    🚀 AniUI Roadmap
                </h2>
                <p className="section-subtitle">
                    Building the React Native ecosystem I wished existed — components to studio
                </p>

                <div className="roadmap-content">
                    {Object.entries(skillsByYear).map(([year, skills]) => (
                        <div key={year} className="year-section">
                            <div className="year-badge">{year}</div>
                            
                            <div className="skills-grid">
                                {skills.map((skill) => (
                                    <div
                                        key={skill.id}
                                        className="roadmap-card"
                                    >
                                        <div className="card-header">
                                            <div className="skill-icon">{skill.icon}</div>
                                            <span className="timeline-badge">{skill.timeline}</span>
                                        </div>
                                        
                                        <h3 className="skill-name">{skill.name}</h3>
                                        <p className="skill-description">{skill.description}</p>
                                        
                                        <span className="category-badge">{skill.category}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="roadmap-goal">
                    <div className="goal-icon">🎯</div>
                    <h3>Vision: Complete React Native Ecosystem</h3>
                    <p>
                        With <strong>8+ years</strong> building web and mobile products, I'm focused on giving React Native
                        the same quality of tooling the web has — open-source components, premium templates, education, and AI-assisted studio.
                    </p>
                    <div className="goal-tags">
                        <span className="tag">149 Components</span>
                        <span className="tag">Open Source</span>
                        <span className="tag">AniUI Pro</span>
                        <span className="tag">Academy</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Roadmap;
